<template>
    <div class="delay-timer-modal">
        <TimerModal
            data-qa="delay-timer-modal"
            :timer-tip-link="$t('funnel.edit.delayTime.help.link')"
            :timer-tip-text="$t('funnel.edit.delayTime.proTip')"
            :title="$t('funnel.flowItemType.timerDelay')"
            @update-config="updateConfig"
            @close-modal="closeModal"
        >
            <template #timerSummary>
                <!-- eslint-disable vue/no-v-html -->
                <span v-html="summaryText" />
                <!-- eslint-enable vue/no-v-html -->
            </template>
            <template #timerParameters>
                <section>
                    <div class="delay-timer-formLabel">
                        {{ $t('funnel.edit.timer.waitAtLeast') }}
                    </div>
                    <div class="delay-wrapper-wait">
                        <DsInputField
                            v-if="hasDelay"
                            v-model="waitDelay"
                            data-qa="delay-timer-wait-delay"
                            type="number"
                            name="number"
                            :submitted="invalidWaitDelay"
                            :required="hasDelay"
                            :step="1"
                            :min="minWaitDelay"
                            :max="maxWaitDelay"
                        >
                            <template #help>
                                <span v-if="invalidWaitDelay" data-qa="invalid-wait-delay">
                                    {{ $t('funnel.edit.timer.invalid.value') }}
                                </span>
                            </template>
                        </DsInputField>
                        <DsMultiselect
                            v-model="waitDelayType"
                            class="wait-delay-type-select"
                            data-qa="delay-timer-wait-delay-type"
                            bind-value-only
                            :options="waitDelayOptions"
                            @input="handleWaitDelayTypeChange"
                        />
                    </div>
                    <div class="delay-timer-formLabel">
                        {{ $t('funnel.edit.timer.runOn') }}
                    </div>
                    <div class="delay-wrapper-run-on-day-week" :class="[{ 'delay-wrapper-run-on-day-of-month': runOnType === $options.RUN_ON_TIME_TYPE_IDS.DayOfMonth }]">
                        <DsMultiselect
                            v-model="runOnType"
                            data-qa="delay-timer-run-on-type"
                            bind-value-only
                            :options="runOnOptions"
                        />
                        <DsMultiselect
                            v-if="runOnType === $options.RUN_ON_TIME_TYPE_IDS.DayOfWeek"
                            v-model="runOnDayOfWeekType"
                            data-qa="delay-timer-run-on-day-week-type"
                            bind-value-only
                            :options="runOnDaysOfWeekOptions"
                        />
                        <DsMultiselect
                            v-if="runOnType === $options.RUN_ON_TIME_TYPE_IDS.DayOfWeek"
                            v-model="runOnDaysOfWeek"
                            data-qa="delay-timer-run-on-day-week"
                            multiple
                            bind-value-only
                            :required="runOnType === $options.RUN_ON_TIME_TYPE_IDS.DayOfWeek"
                            :force-invalid="invalidDayOfWeek"
                            :options="runOnDaysOfWeekInI18nOptions"
                        >
                            <template #help>
                                <span v-if="invalidDayOfWeek" data-qa="invalid-day-of-week">
                                    {{ $t('funnel.edit.timer.required') }}
                                </span>
                            </template>
                        </DsMultiselect>
                        <DsMultiselect
                            v-if="runOnType === $options.RUN_ON_TIME_TYPE_IDS.DayOfMonth"
                            v-model="runOnDaysOfMonth"
                            data-qa="delay-timer-run-on-day-month"
                            multiple
                            bind-value-only
                            :required="runOnType === $options.RUN_ON_TIME_TYPE_IDS.DayOfMonth"
                            :force-invalid="invalidDayOfMonths"
                            :options="daysOfMonthOptions"
                        >
                            <template #help>
                                <span v-if="invalidDayOfMonths" data-qa="invalid-day-of-month">
                                    {{ $t('funnel.edit.timer.required') }}
                                </span>
                            </template>
                        </DsMultiselect>
                    </div>
                    <div class="delay-wrapper-month">
                        <DsMultiselect
                            v-if="isComplexRunOnType"
                            v-model="runOnMonthType"
                            data-qa="delay-timer-run-on-month-type"
                            bind-value-only
                            :options="runOnMonthOptions"
                        />
                        <DsMultiselect
                            v-if="isComplexRunOnType && runOnMonthType === $options.RUN_ON_MONTH.Specific"
                            v-model="runOnMonths"
                            data-qa="delay-timer-run-on-months"
                            multiple
                            bind-value-only
                            :required="isComplexRunOnType && runOnMonthType === $options.RUN_ON_MONTH.Specific"
                            :force-invalid="invalidMonths"
                            :options="monthsIn18nOptions"
                        >
                            <template #help>
                                <span v-if="invalidMonths" data-qa="invalid-month">
                                    {{ $t('funnel.edit.timer.required') }}
                                </span>
                            </template>
                        </DsMultiselect>
                    </div>
                    <div class="timeOfDayType-wrapper">
                        <DsRadio
                            v-model="timeOfDayType"
                            data-qa="delay-timer-time-day-at"
                            val="At"
                        >
                            <span>
                                {{ $t('funnel.edit.timer.timeOfDay.at') }}
                            </span>
                        </DsRadio>
                        <DsRadio
                            v-model="timeOfDayType"
                            data-qa="delay-timer-time-day-between"
                            val="Between"
                            :disabled="waitDelayType !== $options.WAIT_DELAY_TIME_IDS.Minute && waitDelayType !== $options.WAIT_DELAY_TIME_IDS.Hour && waitDelayType !== $options.WAIT_DELAY_TIME_IDS.NoDelay"
                        >
                            <span> {{ $t('funnel.edit.timer.timeOfDay.between') }}</span>
                        </DsRadio>
                        <DsRadio
                            v-model="timeOfDayType"
                            data-qa="delay-timer-time-day-any"
                            val="Any"
                        >
                            <span> {{ $t('funnel.edit.timer.timeOfDay.anyTime') }}</span>
                        </DsRadio>
                    </div>
                    <div class="timer-horizontal-date">
                        <DsDatePicker
                            v-if="timeOfDayType === timeDayTypesCons.AT || timeOfDayType === timeDayTypesCons.BETWEEN"
                            v-model="startTime"
                            data-qa="delay-timer-start-time"
                            time-only
                            name="time-basic"
                            :force-invalid="invalidStartTime"
                            :label="getStartTimeLabel"
                            :allow-input="false"
                            :minute-increment="minuteIncrement"
                            class="small-date-picker"
                            @input="handleStartTimeChange"
                        >
                            <template #error>
                                <span v-if="isMounted && invalidStartTime" data-qa="invalid-start-time">
                                    {{ $t('funnel.edit.timer.invalid') }}
                                </span>
                            </template>
                        </DsDatePicker>
                        <DsDatePicker
                            v-if="timeOfDayType === timeDayTypesCons.BETWEEN"
                            v-model="stopTime"
                            :force-invalid="invalidStopTime || isStopTimeBeforeThanStartTime"
                            data-qa="delay-timer-stop-time"
                            time-only
                            name="time-basic"
                            :label="$t('funnel.delay.timer.endTime')"
                            :allow-input="false"
                            :minute-increment="minuteIncrement"
                            class="small-date-picker"
                            @input="handleStopTimeChange"
                        >
                            <template #error>
                                <span v-if="isMounted && invalidStopTime" data-qa="invalid-stop-time">
                                    {{ $t('funnel.edit.timer.invalid') }}
                                </span>
                                <span v-if="isMounted && isStopTimeBeforeThanStartTime" data-qa="invalid-stop-time">
                                    {{ $t('funnel.edit.timer.invalid.interval') }}
                                </span>
                            </template>
                        </DsDatePicker>
                    </div>
                    <DsCheckbox
                        v-model="useContactTimeZone"
                        data-qa="delay-timer-contact-time-zone"
                    >
                        <span> {{ $t('funnel.edit.timer.useContactTimeZoneWhenAvailable') }}</span>
                    </DsCheckbox>
                </section>
            </template>
        </TimerModal>
    </div>
</template>

<script>
import {
    TIMER_DELAY,
    TIMERS_TIME_INCREMENT,
} from '../../../../utils/editor.constants';

import TimerModal from '../timerModal/TimerModal.vue';

import {
    getWaitDelayTypes, getRunOnTypes, getRunOnDayOfWeekTypes, getWeeksInLocale, getMonthsInLocale,
    getRunDaysOfWeekInI18nOptions, getRunOnMonthTypes, getMonthsInI18n, getDaysOfMonth,
} from '../timers.api';
import { utils } from '../../../../utils/legacy-utils';
import { isValidMinute, TIME_DAY_TYPES, cleanUpTimeFields } from '../timer.utils';

const WAIT_DELAY_CONSTANTS = {
    MINUTE: 'Minute',
    HOUR: 'Hour',
    DAY: 'Day',
    WEEKDAY: 'WeekDay',
    WEEK: 'Week',
    MONTH: 'Month',
    YEAR: 'Year',
    NO_DELAY: 'NoDelay',
    USE_YEAR: 'InField',
};

const ALLOWS_BETWEEN = ['Minute', 'Hour', 'NoDelay'];

export const RUN_ON_TIME_TYPE_IDS = {
    DayOfWeek: 'DayOfWeek',
    DayOfMonth: 'DayOfMonth',
};

export const WAIT_DELAY_TIME_IDS = {
    Minute: 'Minute',
    Hour: 'Hour',
    Day: 'Day',
    Week: 'Week',
    WeekDay: 'WeekDay',
    Month: 'Month',
    Year: 'Year',
    NoDelay: 'NoDelay',
};

export const RUN_ON_MONTH = {
    Specific: 'Specific',
};

export default {
    RUN_ON_TIME_TYPE_IDS,
    WAIT_DELAY_TIME_IDS,
    RUN_ON_MONTH,
    components: {
        TimerModal,
    },

    props: {
        cell: {
            type: Object,
            default: () => ({
                value: {
                    waitDelay: '',
                    waitDelayType: '',
                    runOnType: '',
                    runOnDayOfWeekType: '',
                    runOnDaysOfWeek: [],
                    runOnMonthType: '',
                    runOnMonths: [],
                    runOnDaysOfMonth: [],
                    timeOfDayType: '',
                    startHour: '',
                    startMinute: '',
                    startAmPm: '',
                    stopHour: '',
                    stopMinute: '',
                    stopAmPm: '',
                    useContactTimeZone: false,
                },
            }),
        },
        appInfo: {
            type: Object,
            required: true,
        },
    },

    emits: ['close-modal', 'update-cell-value'],

    data() {
        return {
            isMounted: false,
            timeDayTypesCons: TIME_DAY_TYPES,
            minuteIncrement: Number(TIMERS_TIME_INCREMENT),
            minWaitDelay: 1,
            maxWaitDelay: 365,
            stepDelay: 1,
            waitDelay: this.cell?.value?.waitDelay ?? 3,
            waitDelayType: this.cell?.value?.waitDelayType ?? 'Day',
            runOnType: this.cell.value.runOnType ?? 'Weekday',
            runOnDayOfWeekType: this.cell?.value?.runOnDayOfWeekType ?? 'Any',
            runOnDaysOfWeek: this.cell?.value?.runOnDaysOfWeek ?? [],
            runOnMonthType: this.cell?.value?.runOnMonthType ?? 'Any',
            runOnMonths: this.cell?.value?.runOnMonths ?? [],
            runOnDaysOfMonth: this.cell?.value?.runOnDaysOfMonth ?? [],
            timeOfDayType: this.cell?.value?.timeOfDayType ?? 'At',
            startHour: this.cell?.value?.startHour ?? '08',
            startMinute: this.cell?.value?.startMinute ?? '00',
            startAmPm: this.cell?.value?.startAmPm ?? 'AM',
            stopHour: this.cell?.value?.stopHour ?? '12',
            stopMinute: this.cell?.value?.stopMinute ?? '00',
            stopAmPm: this.cell?.value?.stopAmPm ?? 'PM',
            useContactTimeZone: this.cell?.value?.useContactTimeZone === '1',
            startTime: '',
            stopTime: '',
            cellType: TIMER_DELAY,
            waitDelayOptions: [],
            runOnOptions: [],
            runOnDaysOfWeekOptions: [],
            runOnDaysOfWeekInI18nOptions: [],
            runOnMonthOptions: [],
            monthsIn18nOptions: [],
            daysOfMonthOptions: [],
            timeOfDayOptions: [
                { label: this.$t('funnel.edit.timer.timeOfDay.at'), value: TIME_DAY_TYPES.AT },
                { label: this.$t('funnel.edit.timer.timeOfDay.between'), value: TIME_DAY_TYPES.BETWEEN },
                { label: this.$t('funnel.edit.timer.timeOfDay.anyTime'), value: TIME_DAY_TYPES.ANY },
            ],
            monthsInLocale: [],
            weeksInLocale: [],
            invalidWaitDelay: false,
            invalidDayOfWeek: false,
            invalidMonths: false,
            invalidDayOfMonths: false,
        };
    },

    computed: {
        isStopTimeBeforeThanStartTime() {
            return Number(this.stopTime) <= Number(this.startTime);
        },

        invalidStartTime() {
            return !this.startTime || !this.startMinute || !isValidMinute(this.startMinute);
        },

        invalidStopTime() {
            return !this.stopMinute || !isValidMinute(this.stopMinute);
        },

        hasDelay() {
            return this.waitDelayType !== WAIT_DELAY_CONSTANTS.NO_DELAY;
        },

        summaryText() {
            let summaryText = '';

            const returnData = this.getSummaryLabel();

            const {
                summaryWait, summaryDetailsWait, summaryRunOn, summaryDetailsRunOn, summaryTime, summaryDetailsTime,
            } = returnData;

            let summaryTextTimeZone = '';

            if (this.useContactTimeZone) {
                summaryTextTimeZone = ` ${this.$t('funnel.edit.timer.field.contactTZ')}`;
            } else {
                summaryTextTimeZone = ` ${this.appInfo.appTimeZoneId}`;
            }

            summaryText = `${summaryWait} <b>${summaryDetailsWait}</b> ${summaryRunOn} <b>${summaryDetailsRunOn}</b> ${summaryTime}`;
            summaryText += ` <b>${summaryDetailsTime} ${summaryTextTimeZone}</b>`;

            return summaryText;
        },

        isComplexRunOnType() {
            return this.runOnType === this.$options.RUN_ON_TIME_TYPE_IDS.DayOfWeek
                || this.runOnType === this.$options.RUN_ON_TIME_TYPE_IDS.DayOfMonth;
        },

        getStartTimeLabel() {
            return this.timeOfDayType === this.timeDayTypesCons.BETWEEN ? this.$t('funnel.delay.timer.startTime')
                : this.$t('funnel.delay.timer.time');
        },
    },

    async mounted() {
        await this.fetchModalOptions();
        this.timeLoadTranslation();
    },

    methods: {
        pluralI18n(str, count) {
            if (str === 'NoDelay') {
                return '';
            }

            let label = `delay.timer.${str?.toLowerCase()}`;

            if (count > 1) {
                label = `${label}s`;
            }

            return this.$t(label);
        },

        getSummaryLabel () {
            const hasDelay = this.waitDelayType !== 'NoDelay';
            let summaryWait = '';
            let summaryDetailsWait = '';

            if (hasDelay) {
                summaryWait = this.$t('delay.timer.wait.at.least');
                summaryDetailsWait = `${this.waitDelay} ${this.pluralI18n(this.waitDelayType, this.waitDelay).toLowerCase()}`;
            }

            const summaryRunOnData = this.getRunOnDetails(hasDelay);

            if (!summaryRunOnData.summaryDetailsRunOn) {
                if (hasDelay) {
                    summaryRunOnData.summaryRunOn = `${this.$t('delay.timer.and.then.run')} `;
                } else {
                    summaryRunOnData.summaryRunOn = `${this.$t('delay.timer.run')} `;
                }
                summaryRunOnData.summaryDetailsRunOn = '';
            }

            const summaryTimeData = this.getRunTimeDetails(
                this.timeOfDayType,
                this.startHour,
                this.startMinute,
                this.startAmPm,
                this.stopHour,
                this.stopMinute,
                this.stopAmPm,
                this.$t,
            );

            return {
                summaryWait,
                summaryDetailsWait,
                summaryRunOn: summaryRunOnData.summaryRunOn,
                summaryDetailsRunOn: summaryRunOnData.summaryDetailsRunOn,
                summaryTime: summaryTimeData.summaryTime,
                summaryDetailsTime: summaryTimeData.summaryDetailsTime,
            };
        },

        formatTime(hour, minute, amPm) { // should just use js Date's throughout this whole file
            return `${hour}:${`${minute}`.padStart(2, '0')} ${amPm}`;
        },

        getRunTimeDetails(timeOfDayType, startHour, startMinute, startAmPm, stopHour, stopMinute, stopAmPm, labels) {
            const amPm = labels(`delay.timer.${startAmPm.toLowerCase()}`);
            const startDetails = this.formatTime(startHour, startMinute, amPm);
            let summaryTime = '';
            let summaryDetailsTime = '';

            if (timeOfDayType === 'Between') {
                let stopDetails = '';

                if (stopHour && stopMinute && stopAmPm) {
                    const amPmStop = labels(`delay.timer.${stopAmPm.toLowerCase()}`);

                    stopDetails = this.formatTime(stopHour, stopMinute, amPmStop);
                }

                summaryTime = `${labels('delay.timer.between')} `;
                summaryDetailsTime = `${startDetails} - ${stopDetails !== '' ? stopDetails : 'undefined'}`;
            } else if (timeOfDayType === 'Any') {
                summaryTime = `${labels('delay.timer.at.any.time')} `;
                summaryDetailsTime = '';
            } else {
                summaryTime = `${labels('delay.timer.at')} `;
                summaryDetailsTime = startDetails;
            }

            return { summaryTime, summaryDetailsTime };
        },

        getRunOnDetails (hasDelay) {
            let useMonth = false;

            let summaryRunOn = '';
            let summaryDetailsRunOn = '';

            if (this.runOnType === 'DayOfWeek') {
                if (this.runOnDayOfWeekType === 'Any') {
                    summaryDetailsRunOn = `${summaryDetailsRunOn} ${this.$t('delay.timer.any.lowerCase')}`;
                } else {
                    summaryDetailsRunOn = `${summaryDetailsRunOn} ${this.$t('delay.timer.the')} ${this.$t(`delay.timer.${this.runOnDayOfWeekType.toLowerCase()}.week`)}`;
                }

                const daysOfWeek = utils.toStrI18nWeeks(this.runOnDaysOfWeek, this.weeksInLocale, this.$t);

                summaryDetailsRunOn = `${summaryDetailsRunOn} ${utils.nz(daysOfWeek, this.$t('delay.timer.day'))}`;
                useMonth = true;
            } else if (this.runOnType === 'DayOfMonth') {
                const daysOfMonth = utils.toStr(this.runOnDaysOfMonth, 5, (num) => {
                    return num !== 'Last' ? `${num}${utils.suffix(num)}` : this.$t('delay.timer.last.day');
                }, this.$t);

                const daysOfMonthDetail = utils.is(daysOfMonth) ? `${this.$t('delay.timer.the')} ${daysOfMonth}`
                    : this.$t('delay.timer.any.day');

                summaryDetailsRunOn = `${summaryDetailsRunOn} ${daysOfMonthDetail}`;
                useMonth = true;
            } else if (this.runOnType === 'Weekend') {
                summaryDetailsRunOn = `${summaryDetailsRunOn} ${this.$t('delay.timer.weekend')}`;
            } else if (this.runOnType === 'Weekday') {
                summaryDetailsRunOn = `${summaryDetailsRunOn} ${this.$t('delay.timer.a.weekday')}`;
            }

            if (useMonth) {
                summaryDetailsRunOn = `${summaryDetailsRunOn} `;
                const months = utils.toStrI18nMonths(this.runOnMonths, this.monthsInLocale, this.$t);

                if (this.runOnMonthType === 'Any' || !utils.is(months)) {
                    summaryDetailsRunOn = `${summaryDetailsRunOn} ${this.$t('delay.timer.of.any.month')}`;
                } else {
                    summaryDetailsRunOn = `${summaryDetailsRunOn} ${this.$t('delay.timer.in')} ${months}`;
                }
            }

            if (summaryDetailsRunOn) {
                summaryRunOn = hasDelay ? (this.$t('delay.timer.then.run.on'))
                    : this.$t('delay.timer.run.on');
            }

            return {
                summaryRunOn,
                summaryDetailsRunOn,
            };
        },

        closeModal() {
            this.$emit('close-modal');
        },

        handleStartTimeChange() {
            this.startTimeToAmPmTranslation();
        },

        handleStopTimeChange() {
            this.stopTimeToAmPmTranslation();
        },

        handleWaitDelayTypeChange() {
            if (this.waitDelayType === this.$options.WAIT_DELAY_TIME_IDS.Minute) {
                this.maxWaitDelay = 1440;
                this.minWaitDelay = 1;
                this.waitDelay = 30;
                this.stepDelay = 5;
            } else if (this.waitDelayType === this.$options.WAIT_DELAY_TIME_IDS.Hour) {
                this.maxWaitDelay = 168;
                this.minWaitDelay = 1;
                this.waitDelay = 4;
                this.stepDelay = 1;
            } else if (this.waitDelayType === this.$options.WAIT_DELAY_TIME_IDS.Day || this.waitDelayType === this.$options.WAIT_DELAY_TIME_IDS.WeekDay) {
                this.maxWaitDelay = 365;
                this.minWaitDelay = 1;
                this.waitDelay = 3;
                this.stepDelay = 1;

                if (this.timeOfDayType === this.timeDayTypesCons.BETWEEN) {
                    this.timeOfDayType = this.timeDayTypesCons.AT;
                }
            } else if (this.waitDelayType === this.$options.WAIT_DELAY_TIME_IDS.Week) {
                this.maxWaitDelay = 52;
                this.minWaitDelay = 1;
                this.waitDelay = 1;
                this.stepDelay = 1;

                if (this.timeOfDayType === this.timeDayTypesCons.BETWEEN) {
                    this.timeOfDayType = this.timeDayTypesCons.AT;
                }
            } else if (this.waitDelayType === this.$options.WAIT_DELAY_TIME_IDS.Month) {
                this.maxWaitDelay = 60;
                this.minWaitDelay = 1;
                this.waitDelay = 1;
                this.stepDelay = 1;

                if (this.timeOfDayType === this.timeDayTypesCons.BETWEEN) {
                    this.timeOfDayType = this.timeDayTypesCons.AT;
                }
            } else if (this.waitDelayType === this.$options.WAIT_DELAY_TIME_IDS.Year) {
                this.maxWaitDelay = 25;
                this.minWaitDelay = 1;
                this.waitDelay = 1;
                this.stepDelay = 1;

                if (this.timeOfDayType === this.timeDayTypesCons.BETWEEN) {
                    this.timeOfDayType = this.timeDayTypesCons.AT;
                }
            } else if (this.waitDelayType === this.$options.WAIT_DELAY_TIME_IDS.NoDelay) {
                this.maxWaitDelay = 0;
                this.minWaitDelay = 0;
                this.waitDelay = 0;
                this.stepDelay = 0;
            }

            if (this.waitDelay > this.maxWaitDelay) {
                this.waitDelay = this.maxWaitDelay;
            } else if (this.waitDelay < this.minWaitDelay) {
                this.waitDelay = this.minWaitDelay;
            }
        },

        updateConfig() {
            if (this.isValid()) {
                this.timeSaveTranslation();

                const cell = { ...this.cell };

                cell.value = cell.value || {};
                cell.value.waitDelay = this.waitDelay;
                cell.value.waitDelayType = this.waitDelayType;
                cell.value.runOnType = this.runOnType;
                cell.value.runOnDayOfWeekType = null;
                cell.value.runOnDaysOfWeek = null;
                cell.value.runOnDaysOfMonth = null;
                cell.value.runOnMonthType = null;
                cell.value.runOnMonths = null;
                cell.value.timeOfDayType = this.timeOfDayType;
                cell.value.useContactTimeZone = this.useContactTimeZone ? '1' : '0';
                cell.value.startHour = this.startHour;
                cell.value.startMinute = this.startMinute;
                cell.value.startAmPm = this.startAmPm;
                cell.value.stopHour = null;
                cell.value.stopMinute = null;
                cell.value.stopAmPm = null;

                let useMonth = false;

                if (this.runOnType === this.$options.RUN_ON_TIME_TYPE_IDS.DayOfWeek) {
                    cell.value.runOnDayOfWeekType = this.runOnDayOfWeekType;
                    cell.value.runOnDaysOfWeek = this.runOnDaysOfWeek;
                    useMonth = true;
                } else if (this.runOnType === this.$options.RUN_ON_TIME_TYPE_IDS.DayOfMonth) {
                    cell.value.runOnDaysOfMonth = this.runOnDaysOfMonth;
                    useMonth = true;
                }

                if (useMonth) {
                    cell.value.runOnMonthType = this.runOnMonthType;
                    cell.value.runOnMonths = this.runOnMonths;
                }

                if (this.timeOfDayType === this.timeDayTypesCons.BETWEEN
                    && ALLOWS_BETWEEN.includes(this.waitDelayType)) {
                    cell.value.stopHour = this.stopHour;
                    cell.value.stopMinute = this.stopMinute;
                    cell.value.stopAmPm = this.stopAmPm;
                }

                const labelData = this.getSummaryLabel();
                let cellLabel = '';

                cellLabel += labelData.summaryWait;
                cellLabel += ` ${labelData.summaryDetailsWait}`;
                cellLabel += ` ${labelData.summaryRunOn}`;
                cellLabel += ` ${labelData.summaryDetailsRunOn}`;
                cellLabel += ` ${labelData.summaryTime}`;
                cellLabel += ` ${labelData.summaryDetailsTime}`;

                const labelWithNewLines = cellLabel.replace(/(.{1,25})(\s|$)/g, '$1\n');

                cell.value.name = labelWithNewLines;
                cleanUpTimeFields(cell);

                this.$emit('update-cell-value', { cell });
            }
        },

        async fetchModalOptions() {
            this.waitDelayOptions = await getWaitDelayTypes();
            this.runOnOptions = await getRunOnTypes();
            this.runOnDaysOfWeekOptions = await getRunOnDayOfWeekTypes();
            this.runOnDaysOfWeekInI18nOptions = await getRunDaysOfWeekInI18nOptions();
            this.runOnMonthOptions = await getRunOnMonthTypes();
            this.monthsIn18nOptions = await getMonthsInI18n();
            this.daysOfMonthOptions = await getDaysOfMonth();
            this.weeksInLocale = await getWeeksInLocale();
            this.monthsInLocale = await getMonthsInLocale();
        },

        timeSaveTranslation() {
            this.startTimeToAmPmTranslation();
            this.stopTimeToAmPmTranslation();
        },

        startTimeToAmPmTranslation() {
            if (this.startTime) {
                const hourString = this.startTime.substring(0, 2);
                const minute = this.startTime.substring(2, 4);
                const hour = Number(hourString);

                this.startHour = (hour % 12 || 12).toString();
                this.startMinute = minute.toString();
                this.startAmPm = (hour < 12 ? this.$t('delay.timer.am') : this.$t('delay.timer.pm'));
            }
        },

        stopTimeToAmPmTranslation() {
            if (this.stopTime) {
                const hourString = this.stopTime.substring(0, 2);
                const minute = this.stopTime.substring(2, 4);
                const hour = Number(hourString);

                this.stopHour = (hour % 12 || 12).toString();
                this.stopMinute = minute.toString();
                this.stopAmPm = (hour < 12 ? this.$t('delay.timer.am') : this.$t('delay.timer.pm'));
            }
        },

        timeLoadTranslation() {
            if (this.startHour) {
                let hours = parseInt(this.startHour, 10);
                const minutes = parseInt(this.startMinute, 10);

                hours = (this.startAmPm === this.$t('delay.timer.pm') && hours < 12 ? hours + 12 : hours);
                hours = (this.startAmPm === this.$t('delay.timer.am') && hours === 12 ? hours - 12 : hours);

                let sHours = hours.toString();
                let sMinutes = minutes.toString();

                sHours = (hours < 10 ? `0${sHours}` : sHours);
                sMinutes = (minutes < 10 ? `0${sMinutes}` : sMinutes);

                this.startTime = `${sHours}${sMinutes}`;
            }

            if (this.stopHour) {
                let hours = parseInt(this.stopHour, 10);
                const minutes = parseInt(this.stopMinute, 10);

                hours = (this.stopAmPm === this.$t('delay.timer.pm') && hours < 12 ? hours + 12 : hours);
                hours = (this.stopAmPm === this.$t('delay.timer.am') && hours === 12 ? hours - 12 : hours);

                let sHours = hours.toString();
                let sMinutes = minutes.toString();

                sHours = (hours < 10 ? `0${sHours}` : sHours);
                sMinutes = (minutes < 10 ? `0${sMinutes}` : sMinutes);

                this.stopTime = `${sHours}${sMinutes}`;
            }

            this.isMounted = true;
        },

        isValid() {
            let valid = true;

            this.invalidWaitDelay = false;
            this.invalidDayOfWeek = false;
            this.invalidMonths = false;
            this.invalidDayOfMonths = false;
            this.invalidMonths = false;

            if (this.hasDelay && !this.waitDelay) {
                this.invalidWaitDelay = true;
                valid = false;
            }

            if (this.runOnType === this.$options.RUN_ON_TIME_TYPE_IDS.DayOfWeek) {
                if (!this.runOnDaysOfWeek?.length) {
                    this.invalidDayOfWeek = true;
                    valid = false;
                }

                if (this.runOnMonthType === this.$options.RUN_ON_MONTH.Specific && (!this.runOnMonths?.length)) {
                    this.invalidMonths = true;
                    valid = false;
                }
            }

            if (this.runOnType === this.$options.RUN_ON_TIME_TYPE_IDS.DayOfMonth) {
                if (!this.runOnDaysOfMonth?.length) {
                    this.invalidDayOfMonths = true;
                    valid = false;
                }

                if (this.runOnMonthType === this.$options.RUN_ON_MONTH.Specific && (!this.runOnMonths?.length)) {
                    this.invalidMonths = true;
                    valid = false;
                }
            }

            if (this.timeOfDayType === this.timeDayTypesCons.AT) {
                if (this.invalidStartTime) {
                    valid = false;
                }
            } else if (this.timeOfDayType === this.timeDayTypesCons.BETWEEN && ALLOWS_BETWEEN.includes(this.waitDelayType)) {
                if (this.invalidStartTime || this.invalidStopTime) {
                    valid = false;
                }

                if (this.isStopTimeBeforeThanStartTime) {
                    valid = false;
                }
            }

            return valid;
        },
    },
};
</script>

<style lang="scss" scoped>
:deep(.assistive-text) {
    color: $color-red-800;
}

.wait-delay-type-select {
    --multiselect-menu-width: 150px;
    :deep(.menu) { // css bleed
        overflow-x: hidden;
    }
}
.delay-wrapper-wait, .timeOfDayType-wrapper {
    display: inline-flex;
    grid-gap: 1rem;
}

.delay-wrapper-run-on-day-week {
    display: grid;
    grid-template-columns: 2fr 1fr 3fr;
    grid-gap: 1rem;
    margin-bottom: $spacing-300;

    .multiselect {
        margin-bottom: 0;
    }
}

.delay-wrapper-run-on-day-of-month {
    display: grid;
    grid-template-columns: 1fr 2fr;
    grid-gap: 1rem;
    margin-bottom: $spacing-300;

    .multiselect {
        margin-bottom: 0;
    }
}

.delay-wrapper-month {
    display: grid;
    grid-template-columns: 1fr 2fr;
    grid-gap: 1rem;
    border-bottom: 1px solid $color-gray-lighter;
    padding-bottom: $spacing-300;
    margin-bottom: $spacing-300;

    .multiselect {
        margin-bottom: 0;
    }
}

div.delayTimer-status > div.dropdown >  .dropdown-menu.open {
    width: 100%;
}

.delay-timer-formLabel {
    font-size: $font-size-md;
    margin-bottom: $spacing-200;
    border-top: 1px solid $color-gray-lighter;
    padding-top: $spacing-200;
}

.timer-horizontal-date {
    display: flex;
    padding: 10px 0;
}

.small-date-picker {
    max-width: 125px;
    margin-right: 30px;
}

:deep(.small-date-picker .error .assistive-text) {
    color: $color-red-800;
}
:deep(.small-date-picker .error .form-control) {
    color: $color-red-800;
}

.delay-timer-modal .delay-wrapper-run-on-day-week,
.delay-timer-modal .delay-wrapper-run-on-day-of-month,
.delay-timer-modal .delay-wrapper-month {
    :deep(.multiselect.multiple .multiselect-tags) {
        padding: 0 0 0 0.5rem;
    }
}
</style>
